import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingPageNavbar from './components/LandingPageNavbar';
import PlatformHeader from '../../components/ui/PlatformHeader';
import AuthenticationModal from '../../components/ui/AuthenticationModal';
import HeroSection from './components/HeroSection';
import CampaignsSection from './components/CampaignsSection';
import FeaturesSection from './components/FeaturesSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import TestimonialsSection from './components/TestimonialsSection';
import { useAuth } from '../../contexts/AuthContext';
import type { Campaign } from './types';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  // Auto-redirect logged-in users to their dashboard
  useEffect(() => {
    if (!loading && user && userProfile) {
      const dashboardRoutes: Record<string, string> = {
        brand: '/brand-dashboard',
        creator: '/creator-dashboard',
        admin: '/admin-dashboard',
        agency: '/agency-dashboard',
      };
      const redirectTo = dashboardRoutes[userProfile.role] || '/';
      navigate(redirectTo, { replace: true });
    }
  }, [user, userProfile, loading, navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadCampaigns = async () => {
      try {
        // Remove campaignService.getAllCampaigns and local mapping
        // CampaignsSection handles its own data fetching
        if (isMounted) {
          setCampaigns([]);
        }
      } catch (err) {
        console.error('Error loading campaigns:', err);
      } finally {
        if (isMounted) {
          setPageLoading(false);
        }
      }
    };

    loadCampaigns();

    return () => {
      isMounted = false;
    };
  }, []);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const handleAuthSuccess = () => {
    setIsAuthModalOpen(false);
  };

  const handleLogout = () => {
    // Logout logic for non-authenticated users (no-op in this case)
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {user && userProfile ? <PlatformHeader /> : <LandingPageNavbar />}
      <main className="relative">
        <HeroSection />
        <section id="campaigns" className="scroll-mt-20">
          <CampaignsSection />
        </section>
        <section id="features" className="scroll-mt-20">
          <FeaturesSection />
        </section>
        <section id="testimonials" className="scroll-mt-20">
          <TestimonialsSection />
        </section>
        <section id="about" className="scroll-mt-20">
          <CTASection />
        </section>
      </main>
      <Footer />
      <AuthenticationModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default LandingPage;